import { ensureAnonSession, supabase } from '../lib/supabase'
import { isStaffPreview } from './previewMode'
import type { ChangeRequest } from '../types/portal'

/**
 * Shared review data.
 *
 * Change requests and approvals must look the same to everyone holding a
 * project link, so they live in Supabase rather than in one browser.
 *
 * Access works exactly as the database expects it to: the visitor gets an
 * anonymous session, then exchanges the project slug and the access token from
 * their link for the project UUID via the join_project RPC. Everything after
 * that is addressed by real UUIDs. The token is never read back out, stored in
 * app state, or shown anywhere.
 *
 * Every call is defensive: if RLS or a missing row blocks a request we log what
 * failed and let the interface carry on locally, so the client never loses
 * their input.
 */

interface JoinedProject {
  /** projects.id */
  id: string
  /** deliverables.deliverable_key → deliverables.id */
  deliverableIds: Map<string, string>
}

/** One joined project per browser session, keyed by slug. */
const joined = new Map<string, JoinedProject>()
const pending = new Map<string, Promise<JoinedProject | null>>()

function warn(what: string, error: { message: string; code?: string } | null | unknown) {
  if (!error) return
  const detail = error as { message?: string; code?: string }
  console.warn(
    `[supabase] ${what} failed: ${detail.message ?? String(error)}${
      detail.code ? ` (${detail.code})` : ''
    }`,
  )
}

/** The access token on the current client link, e.g. /project/<slug>?access=<uuid>. */
function accessTokenFromUrl(): string {
  if (typeof window === 'undefined') return ''
  const params = new URLSearchParams(window.location.search)
  return params.get('access') ?? params.get('token') ?? ''
}

/** The project UUID for this slug, joining first if necessary. */
async function joinProject(
  slug: string,
  clientName: string,
  suppliedToken = '',
): Promise<JoinedProject | null> {
  if (!supabase || !slug) return null

  const existing = joined.get(slug)
  if (existing) return existing

  const inFlight = pending.get(slug)
  if (inFlight) return inFlight

  const request = (async () => {
    /* Staff previewing a client link never join it: joining records the
       viewer as a member of the project, and a preview must write nothing.
       Dropping the token takes the read-only lookup below instead, which the
       project's existing RLS already permits Portal staff. */
    const token = isStaffPreview() ? '' : suppliedToken || accessTokenFromUrl()
    let projectId: string | null = null

    if (token) {
      /* Client arriving on a project link: exchange slug + token for the project. */
      await ensureAnonSession()

      const { data, error } = await supabase.rpc('join_project', {
        project_slug: slug,
        project_token: token,
        client_name: clientName || 'Client',
      })
      warn('join_project', error)

      // The RPC returns the project UUID, either bare or on a row.
      projectId =
        typeof data === 'string'
          ? data
          : Array.isArray(data)
            ? (data[0]?.id ?? data[0]?.project_id ?? null)
            : ((data as { id?: string; project_id?: string } | null)?.id ??
              (data as { project_id?: string } | null)?.project_id ??
              null)
    } else {
      /* Studio side: no link token, so resolve the project directly. Signed-in
         staff are permitted this by the project's existing RLS policies. */
      const { data, error } = await supabase
        .from('projects')
        .select('id')
        .eq('slug', slug)
        .maybeSingle()
      warn('project lookup', error)
      projectId = (data?.id as string | undefined) ?? null
    }

    if (!projectId) return null

    const record: JoinedProject = { id: projectId, deliverableIds: new Map() }

    const { data: rows, error: deliverableError } = await supabase
      .from('deliverables')
      .select('id, deliverable_key')
      .eq('project_id', projectId)
    warn('deliverable lookup', deliverableError)

    rows?.forEach((row: { id: string; deliverable_key: string | null }) => {
      if (row.deliverable_key) record.deliverableIds.set(row.deliverable_key, row.id)
    })

    joined.set(slug, record)
    return record
  })()

  pending.set(slug, request)
  const result = await request
  pending.delete(slug)
  return result
}

/**
 * The database id for one deliverable within an already-joined project.
 *
 * The id map is built once, when the tab first joins the project. A
 * deliverable staff add afterward — while that tab is still open — won't be
 * in it yet, and a plain cache miss used to mean the write silently did
 * nothing. This looks the one key up directly instead, and caches it once
 * found, so a tab that's been open for a while still works.
 */
async function resolveDeliverableId(
  project: JoinedProject,
  deliverableKey: string,
): Promise<string | null> {
  const cached = project.deliverableIds.get(deliverableKey)
  if (cached) return cached
  if (!supabase) return null

  const { data, error } = await supabase
    .from('deliverables')
    .select('id')
    .eq('project_id', project.id)
    .eq('deliverable_key', deliverableKey)
    .maybeSingle()
  warn('deliverable lookup (refresh)', error)
  if (!data?.id) return null

  project.deliverableIds.set(deliverableKey, String(data.id))
  return String(data.id)
}

export type ProjectAccess = JoinedProject

/**
 * The project UUID and its deliverable ids, resolved through exactly the same
 * access path as change requests and approvals. Shared so other stored review
 * data (transcripts) uses one access model rather than inventing another.
 */
export async function projectAccess(
  slug: string,
  clientName: string,
  suppliedToken = '',
): Promise<ProjectAccess | null> {
  return joinProject(slug, clientName, suppliedToken)
}

/**
 * Joins the project for this link. Called when the client enters their name;
 * safe to call repeatedly.
 */
export async function openProjectSession(
  slug: string,
  clientName: string,
  suppliedToken = '',
): Promise<string | null> {
  const record = await joinProject(slug, clientName, suppliedToken)
  return record?.id ?? null
}

/** Forgets the joined session — used when the remembered name is cleared. */
export function closeProjectSession(): void {
  joined.clear()
  pending.clear()
}

function timeOf(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function stampOf(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return `${date.toLocaleDateString([], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })} · ${timeOf(iso)}`
}

/**
 * Marks one stored change request completed. The row is updated, never
 * deleted, so the history stays intact for everyone.
 */
export async function resolveChangeRequest(requestId: string): Promise<void> {
  if (!supabase) return

  const { error } = await supabase
    .from('change_requests')
    .update({ resolved: true })
    .eq('id', requestId)

  warn('change request resolve', error)
}

export interface RemoteActivity {
  /** Stable, derived from the source row, so refreshing never duplicates it. */
  id: string
  projectSlug: string
  projectName: string
  actorName: string
  action: string
  deliverableKey: string
  message?: string
  at: number
}

/**
 * Studio activity, derived from the change_requests and approvals already
 * stored — nothing is written twice. Signed-in staff read this under the
 * project's existing RLS policies.
 */
export async function fetchStaffActivity(): Promise<RemoteActivity[]> {
  if (!supabase) return []

  const [requests, approvals] = await Promise.all([
    supabase
      .from('change_requests')
      .select(
        'id, display_name, comment, created_at, projects(client_name, slug), deliverables(deliverable_key, title)',
      )
      .order('created_at', { ascending: false })
      .limit(30),
    supabase
      .from('approvals')
      .select(
        'id, display_name, version, approved_at, projects(client_name, slug), deliverables(deliverable_key, title)',
      )
      .order('approved_at', { ascending: false })
      .limit(30),
  ])

  warn('activity · change requests', requests.error)
  warn('activity · approvals', approvals.error)

  type Joined = {
    projects?: { client_name?: string; slug?: string } | null
    deliverables?: { deliverable_key?: string; title?: string } | null
  }

  const rows: RemoteActivity[] = []

  requests.data?.forEach((row: Record<string, unknown> & Joined) => {
    const project = row.projects
    const deliverable = row.deliverables
    const at = new Date(String(row.created_at ?? '')).getTime()
    if (!project?.slug || Number.isNaN(at)) return
    rows.push({
      id: `cr-${String(row.id)}`,
      projectSlug: project.slug,
      projectName: project.client_name ?? '',
      actorName: String(row.display_name ?? 'Client'),
      action: `requested changes on ${deliverable?.title ?? 'a deliverable'}`,
      deliverableKey: deliverable?.deliverable_key ?? '',
      message: String(row.comment ?? '') || undefined,
      at,
    })
  })

  approvals.data?.forEach((row: Record<string, unknown> & Joined) => {
    const project = row.projects
    const deliverable = row.deliverables
    const at = new Date(String(row.approved_at ?? '')).getTime()
    if (!project?.slug || Number.isNaN(at)) return
    rows.push({
      id: `ap-${String(row.id)}`,
      projectSlug: project.slug,
      projectName: project.client_name ?? '',
      actorName: String(row.display_name ?? 'Client'),
      action: `approved ${deliverable?.title ?? 'a deliverable'}`,
      deliverableKey: deliverable?.deliverable_key ?? '',
      at,
    })
  })

  return rows.sort((a, b) => b.at - a.at)
}

export interface RemoteApproval {
  deliverableKey: string
  displayName: string
  version: string
  /** Epoch ms of the approval, used to show how long it has been waiting. */
  at?: number
}

export interface RemoteChangeRequest extends ChangeRequest {
  deliverableKey: string
}

/** Insert one change request. Returns the stored row id, or null if it was not saved. */
export async function insertChangeRequest(input: {
  projectSlug: string
  deliverableKey: string
  displayName: string
  comment: string
  timecode: string
  version: string
}): Promise<string | null> {
  if (!supabase) return null

  const project = await joinProject(input.projectSlug, input.displayName)
  if (!project) return null
  const deliverableId = await resolveDeliverableId(project, input.deliverableKey)
  if (!deliverableId) return null

  const userId = await ensureAnonSession()

  const { data, error } = await supabase
    .from('change_requests')
    .insert({
      project_id: project.id,
      deliverable_id: deliverableId,
      user_id: userId,
      display_name: input.displayName,
      timecode: input.timecode,
      comment: input.comment,
      version: input.version,
    })
    .select('id')
    .single()

  warn('change request insert', error)
  return (data?.id as string) ?? null
}

/** Every change request for a project, oldest first. */
export async function fetchChangeRequests(
  projectSlug: string,
  clientName: string,
): Promise<RemoteChangeRequest[]> {
  if (!supabase) return []

  const project = await joinProject(projectSlug, clientName)
  if (!project) return []

  const keyOf = new Map(
    Array.from(project.deliverableIds.entries()).map(([key, id]) => [id, key]),
  )

  const { data, error } = await supabase
    .from('change_requests')
    .select('id, deliverable_id, display_name, timecode, comment, version, resolved, created_at')
    .eq('project_id', project.id)
    .order('created_at', { ascending: true })

  warn('change request fetch', error)
  if (!data) return []

  return data.map((row: Record<string, unknown>) => {
    const created = String(row.created_at ?? '')
    return {
      id: String(row.id),
      deliverableKey: keyOf.get(String(row.deliverable_id)) ?? '',
      body: String(row.comment ?? ''),
      timecode: String(row.timecode ?? ''),
      authorName: String(row.display_name ?? 'Client'),
      time: timeOf(created),
      submitted: stampOf(created),
      at: created ? new Date(created).getTime() : undefined,
      version: String(row.version ?? ''),
      resolved: Boolean(row.resolved),
      shared: true,
    }
  })
}

/**
 * Records an approval. approved_at is populated by the database.
 *
 * Returns whether it was actually stored — the caller decides how to tell the
 * person, rather than this silently doing nothing on a stale id or a refusal.
 */
export async function insertApproval(input: {
  projectSlug: string
  deliverableKey: string
  displayName: string
  version: string
}): Promise<boolean> {
  if (!supabase) return false

  const project = await joinProject(input.projectSlug, input.displayName)
  if (!project) return false
  const deliverableId = await resolveDeliverableId(project, input.deliverableKey)
  if (!deliverableId) return false

  const userId = await ensureAnonSession()

  const { error } = await supabase.from('approvals').insert({
    project_id: project.id,
    deliverable_id: deliverableId,
    user_id: userId,
    display_name: input.displayName,
    version: input.version,
  })

  warn('approval insert', error)
  return !error
}

/** Every approval for a project, oldest first. */
export async function fetchApprovals(
  projectSlug: string,
  clientName: string,
): Promise<RemoteApproval[]> {
  if (!supabase) return []

  const project = await joinProject(projectSlug, clientName)
  if (!project) return []

  const keyOf = new Map(
    Array.from(project.deliverableIds.entries()).map(([key, id]) => [id, key]),
  )

  const { data, error } = await supabase
    .from('approvals')
    .select('deliverable_id, display_name, version, approved_at')
    .eq('project_id', project.id)
    .order('approved_at', { ascending: true })

  warn('approval fetch', error)
  if (!data) return []

  return data.map((row: Record<string, unknown>) => {
    const approved = String(row.approved_at ?? '')
    return {
      deliverableKey: keyOf.get(String(row.deliverable_id)) ?? '',
      displayName: String(row.display_name ?? 'Client'),
      version: String(row.version ?? ''),
      at: approved ? new Date(approved).getTime() : undefined,
    }
  })
}
